import BaseController from "@base/baseController.js";
import ShowcaseManager from "./showcaseManager.js";
import ShowcasePage from "./components/ShowcasePage.js";
import ItemEditor from "./components/ItemEditor.js";
import GearDetail from "./components/GearDetail.js";
import api from "@api/api.js";
import router from "@/services/router.js";

/**
 * Showcase module controller: loads gear items and mounts either the gear pile
 * or an item's detail page. Navigation goes through the router (#/gear and
 * #/gear/:id); the edit overlay mounts straight onto document.body like the
 * gallery editor does.
 */
class ShowcaseController extends BaseController {
  constructor() {
    super({ manager: new ShowcaseManager() });
    this._loaded = false;
  }

  isAdmin() {
    return this.manager.admin;
  }

  async ensureLoaded() {
    if (!this._loaded) {
      await this.manager.load();
      this._loaded = true;
    }
  }

  /** Force a reload on next access (e.g. after login/language change). */
  invalidate() {
    this._loaded = false;
  }

  /** The public gear pile — view-only; admin actions live in the Cockpit. */
  async showPage(container, lang) {
    await this.ensureLoaded();
    this.view = new ShowcasePage({
      items: this.manager.all(),
      lang,
      admin: this.manager.admin,
      onOpen: (id) => router.go(`/gear/${id}`),
    });
    await this.view.mount(container);
    return this.view;
  }

  /**
   * Open the create/edit overlay. `onDone` lets the caller (the Cockpit's Gear
   * Manager) refresh itself; the default patches a mounted gear pile in place
   * rather than reloading the page.
   */
  openEditor(item, onDone) {
    new ItemEditor({
      item,
      onDone: onDone || (() => this.refreshAfterEdit()),
    }).mount(document.body);
  }

  /**
   * Re-fetch items and patch them into the mounted pile in place. `this.view`
   * may also be a detail page, which has nothing to patch — hence the optional
   * call rather than an unconditional one.
   */
  async refreshAfterEdit() {
    this.invalidate();
    await this.ensureLoaded();
    this.view?.refreshItems?.(this.manager.all());
  }

  /**
   * A single gear item's page. The manager already holds every item this viewer
   * may see (the overview is filtered server-side), so an id that isn't there
   * is either unknown or not visible — indistinguishable, and both belong back
   * on the pile rather than on an error page.
   */
  async showDetail(container, id, lang) {
    await this.ensureLoaded();
    const item = this.manager.get(id);
    if (!item) {
      router.go("/gear");
      return null;
    }
    this.view = new GearDetail({ item, lang });
    await this.view.mount(container);
    return this.view;
  }

  async reorder(orderedIds) {
    await api.post("/api/showcase/reorder", { orderedIds });
    this.invalidate();
  }
}

export default new ShowcaseController();
